"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";

const ownerFeatures = [
  { icon: "💰", text: "Revenue optimization & dynamic pricing" },
  { icon: "🏠", text: "Full-service property management" },
  { icon: "⭐", text: "5-star turnover cleaning" },
  { icon: "📊", text: "Monthly performance reports" },
];

const renterFeatures = [
  { icon: "🌊", text: "Beachfront & gulf-view properties" },
  { icon: "✨", text: "Hotel-level cleanliness guaranteed" },
  { icon: "🔑", text: "Seamless check-in & check-out" },
  { icon: "📞", text: "24/7 guest support" },
];

export default function DualAudienceFlow() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<"owner" | "renter">("owner");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef?.current?.querySelectorAll(".reveal-hidden");
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="section-padding bg-sand">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-12 reveal-hidden">
          <span className="text-gold text-xs font-bold uppercase tracking-widest block mb-3">
            Who We Serve
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl text-navy mb-4">
            Built for{" "}
            <span className="italic font-light text-gold">Both Sides</span>
          </h2>
          <p className="text-navy/60 text-lg max-w-xl mx-auto">
            Whether you own a PCB property or planning your next vacation — we have you covered.
          </p>
        </div>

        {/* Tab Toggle */}
        <div className="flex justify-center mb-10 reveal-hidden">
          <div className="inline-flex bg-navy/8 rounded-full p-1.5 border border-navy/10">
            <button
              onClick={() => setActiveTab("owner")}
              className={`px-8 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
                activeTab === "owner" ?"bg-navy text-white shadow-lg" :"text-navy/60 hover:text-navy"
              }`}
            >
              🏡 Property Owners
            </button>
            <button
              onClick={() => setActiveTab("renter")}
              className={`px-8 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
                activeTab === "renter" ?"bg-gold text-navy shadow-lg" :"text-navy/60 hover:text-navy"
              }`}
            >
              🌴 Vacationers
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 reveal-hidden">
          {/* Owner Card */}
          <div
            className={`relative rounded-3xl overflow-hidden transition-all duration-500 ${
              activeTab === "owner" ?"ring-2 ring-gold shadow-2xl scale-[1.02]" :"opacity-60 scale-[0.98]"
            }`}
          >
            <div className="bg-gradient-to-br from-navy to-navy-dark p-8 sm:p-10 h-full">
              <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 bg-gold/20 border border-gold/30 rounded-full px-4 py-1.5 mb-6">
                  <span className="text-gold text-xs font-bold uppercase tracking-widest">For Owners</span>
                </div>
                <h3 className="font-heading text-3xl text-white mb-3">
                  Maximize Your{" "}
                  <span className="text-gold italic font-light">Rental Income</span>
                </h3>
                <p className="text-white/65 text-base leading-relaxed mb-8">
                  We handle every aspect of your short-term rental — from 5-star turnovers to dynamic pricing — so you earn more while doing absolutely nothing.
                </p>
                <ul className="space-y-3 mb-8">
                  {ownerFeatures?.map((f, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <span className="text-xl">{f?.icon}</span>
                      <span className="text-white/80 text-sm">{f?.text}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    href="#roi-calculator"
                    className="btn-gold px-6 py-3 rounded-full font-semibold text-sm text-center"
                  >
                    Estimate My Income
                  </Link>
                  <Link
                    href="/contact"
                    className="btn-ghost px-6 py-3 rounded-full font-medium text-sm text-center"
                  >
                    Get Free Consultation
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Renter Card */}
          <div
            className={`relative rounded-3xl overflow-hidden transition-all duration-500 ${
              activeTab === "renter" ?"ring-2 ring-gold shadow-2xl scale-[1.02]" :"opacity-60 scale-[0.98]"
            }`}
          >
            <div className="bg-gradient-to-br from-sky-900 to-navy-dark p-8 sm:p-10 h-full">
              <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-blue-400/10 blur-3xl pointer-events-none" />
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 bg-blue-400/20 border border-blue-400/30 rounded-full px-4 py-1.5 mb-6">
                  <span className="text-blue-300 text-xs font-bold uppercase tracking-widest">For Vacationers</span>
                </div>
                <h3 className="font-heading text-3xl text-white mb-3">
                  Your Perfect{" "}
                  <span className="text-blue-300 italic font-light">PCB Getaway</span>
                </h3>
                <p className="text-white/65 text-base leading-relaxed mb-8">
                  Stay in a professionally managed, hotel-clean vacation rental steps from the Gulf. Every property is inspected and guest-ready before your arrival.
                </p>
                <ul className="space-y-3 mb-8">
                  {renterFeatures?.map((f, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <span className="text-xl">{f?.icon}</span>
                      <span className="text-white/80 text-sm">{f?.text}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    href="/contact"
                    className="bg-blue-500 hover:bg-blue-400 text-white px-6 py-3 rounded-full font-semibold text-sm text-center transition-all duration-300 hover:-translate-y-0.5"
                  >
                    Inquire About Rentals
                  </Link>
                  <Link
                    href="/contact"
                    className="btn-ghost px-6 py-3 rounded-full font-medium text-sm text-center"
                  >
                    Ask a Question
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
